import { listLeases } from "src/graphql/queries";
import { API } from "aws-amplify";
import { GRAPHQLFilters, LoaderData } from "src/types";
import { getGraphqlFilters } from "src/utils";
import * as APITypes from "src/API";
import dayjs from "dayjs";

export const EMPTY_FORMATTED_LEASE: APITypes.Lease = {
  id: "",
  entityID: "",
  entityType: APITypes.EntityType.Unit,
  type: APITypes.LeaseTypeOptions.Monthly,
  status: APITypes.LeaseStatusType.Rejected,
  tenantID: "",
  startDate: "",
  endDate: "",
  markedDate: "",
  extraOccupants: [""],
  userID: "",
  applicationID: "",
  backgroundCheckID: "",
  offerID: "",
  moveInID: "",
  moveOutID: "",
  nextLeaseID: "",
  prevLeaseID: "",
  __typename: "Lease",
  createdAt: "",
  updatedAt: "",
  _version: 0,
  _lastChangedAt: 0,
};

/**
 * Retrieves the active lease details for a specific tenant.
 *
 * This function fetches all leases associated with a tenant and returns the currently active lease.
 * If multiple leases exist, it selects the lease where:
 * - The current date falls between the lease start and end dates
 * - The lease status is 'Active'
 *
 * @param tenantID - The unique identifier of the tenant
 * @returns Promise<{success: boolean, data: APITypes.Lease, error?: any}> - Object containing:
 *   - success: Boolean indicating if the operation was successful
 *   - data: The active lease object or EMPTY_FORMATTED_LEASE if no lease found
 *   - error: Error object if the operation failed
 */
export const getActiveLease = async (
  tenantID: string
): Promise<LoaderData<APITypes.Lease>> => {
  let filters: GRAPHQLFilters = getGraphqlFilters({ tenantID });
  try {
    const leaseResponse = (await API.graphql({
      query: listLeases,
      variables: {
        filter: filters,
      },
    })) as { data?: APITypes.ListLeasesQuery };
    if (
      !leaseResponse.data?.listLeases?.items ||
      leaseResponse.data.listLeases.items.length <= 0
    ) {
      throw new Error("No lease found"); // TODO [Translate]
    }
    let count = 0;
    // if there's more than one lease, then grabs the first one that the current date is active within and lease is active
    if (leaseResponse.data.listLeases.items.length > 1) {
      const date = new Date();
      const dateFormat = "YYYY/MM/DD";
      for (let i = 0; i < leaseResponse.data.listLeases.items.length; i++) {
        if (
          dayjs(
            leaseResponse.data.listLeases.items[i]?.startDate?.toString(),
            dateFormat
          ).isBefore(date) &&
          dayjs(
            leaseResponse.data.listLeases.items[i]?.endDate?.toString(),
            dateFormat
          ).isAfter(date) &&
          leaseResponse.data.listLeases.items[i]?.status ===
            APITypes.LeaseStatusType.Active
        ) {
          count = i;
          break;
        }
      }
    }
    return {
      success: true,
      data: leaseResponse.data.listLeases.items[count] || EMPTY_FORMATTED_LEASE,
    };
  } catch (error) {
    console.error("Failed to get lease details", error);
    return {
      success: false,
      data: EMPTY_FORMATTED_LEASE,
      error,
    };
  }
};
